/**
 * Helpers para manipulacao de imagens antes de upload.
 *
 * Reduz tamanho de arquivo (resize + compress) pra economizar bandwidth
 * e storage. Roda em mobile (iOS/Android) e Expo Web.
 *
 * Uso tipico:
 *   const compressed = await compressImageForUpload(pickerResult.assets[0].uri);
 *   const response = await fetch(compressed.uri);
 *   const blob = await response.blob();
 *   await supabase.storage.from('avatars').upload(path, blob, { contentType: 'image/jpeg' });
 *
 * Falha-safe: em caso de erro, retorna URI original (upload acontece sem comprimir
 * mas nao quebra o fluxo do usuario).
 */
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';

export interface CompressOptions {
  /** Max largura em pixels. Altura calculada automaticamente pra preservar aspect ratio. Default 800. */
  maxWidth?: number;
  /** Qualidade JPEG 0-1. 0.85 e suficiente pra avatar/profile, 0.75 pra fotos em galeria. Default 0.85. */
  quality?: number;
}

export interface CompressResult {
  uri: string;
  width: number;
  height: number;
}

/**
 * Comprime imagem pra upload com resize + JPEG compression.
 *
 * @param uri URI local da imagem (do ImagePicker, asset library, etc)
 * @param options maxWidth (default 800) + quality (default 0.85)
 * @returns objeto com URI da imagem comprimida (file:// no mobile, blob: no web)
 */
export async function compressImageForUpload(
  uri: string,
  options: CompressOptions = {}
): Promise<CompressResult> {
  const { maxWidth = 800, quality = 0.85 } = options;

  try {
    const ctx = ImageManipulator.manipulate(uri);
    ctx.resize({ width: maxWidth });
    const ref = await ctx.renderAsync();
    const result = await ref.saveAsync({
      format: SaveFormat.JPEG,
      compress: quality,
    });
    return { uri: result.uri, width: result.width, height: result.height };
  } catch (e) {
    if (__DEV__) {
      console.warn('[image-utils] compressao falhou, usando URI original:', e);
    }
    // Fallback: upload acontece sem compressao
    return { uri, width: 0, height: 0 };
  }
}
